import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { IMAGES } from "@/lib/site";

const TEAM_VALUES = [
  { num: "01", title: "Everyone can grow", body: "We believe every person — regardless of history or starting point — has the capacity to build new skills." },
  { num: "02", title: "Skills over stigma", body: "We focus on what someone can learn and become, not what's wrong with them." },
  { num: "03", title: "Engagement first", body: "Real change starts with showing up. We meet people where they are, and earn the engagement before the work." },
  { num: "04", title: "A life worth living", body: "Our goal isn't just outcomes on paper — it's an ideal life, defined by the person themselves." },
];

export default function About() {
  return (
    <div data-testid="page-about">
      <section className="bg-warm-50 pt-24 pb-20 lg:pt-32 lg:pb-24">
        <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-12">
          <p className="text-xs uppercase tracking-[0.25em] text-brand font-semibold mb-6">About Neurogility</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-ink-900 max-w-4xl">
            A Melbourne community services organisation, built on the belief <em className="text-brand not-italic">everyone can grow</em>.
          </h1>
          <p className="mt-8 text-lg lg:text-xl leading-relaxed text-ink-500 max-w-3xl">
            Neurogility was founded to do one thing well: help people build the practical, social and emotional
            skills they need for the life they actually want. We're holistic by design, community-led, and
            specialise in engaging the people other services find hardest to reach.
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6">
            <img src={IMAGES.team} alt="Clinical team" className="rounded-2xl w-full h-[520px] object-cover" />
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-6 lg:pl-6">
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight leading-tight text-ink-900">
              A qualified, experienced and committed team.
            </h2>
            <p className="mt-5 text-lg text-ink-500 leading-relaxed">
              Neurogility is led by an <strong className="text-ink-900 font-semibold">ACA-registered counsellor</strong>,
              supported by qualified facilitators across cooking, outdoor recreation, group counselling and
              community engagement. Every staff member brings real-world experience and a commitment to
              showing up for the people we work with.
            </p>
            <ul className="mt-8 space-y-2 text-ink-700">
              {[
                "ACA-registered counsellor (Founder & Clinical Lead)",
                "Qualified group counselling facilitators",
                "Experienced outdoor recreation practitioners",
                "Trained cooking & life-skills coaches",
                "Justice & community engagement specialists",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 py-1">
                  <span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-brand" /> {t}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-warm-50">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.2em] text-brand font-semibold mb-5">What we believe</p>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight leading-tight text-ink-900 max-w-3xl">
              Four principles guiding every plan we write.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-warm-200/60 rounded-2xl overflow-hidden border border-warm-200/60">
            {TEAM_VALUES.map((v, i) => (
              <Reveal key={v.num} delay={i * 0.05}>
                <div data-testid={`value-${v.num}`} className="bg-warm-50 p-10 lg:p-12 h-full">
                  <p className="font-serif text-5xl text-brand/60 mb-6">{v.num}</p>
                  <h3 className="font-serif text-2xl text-ink-900 mb-3">{v.title}</h3>
                  <p className="text-ink-500 leading-relaxed">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-ink-900 text-white">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-12 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight">
            Want to see how we'd support your participant?
          </h2>
          <Link
            to="/professionals"
            data-testid="about-refer-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-soft text-ink-900 px-7 py-4 text-sm font-semibold hover:bg-white transition-colors"
          >
            Begin a referral <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
